import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import './styles.scss';

// ─────────────────────────────────────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────────────────────────────────────

const featureCards = [
  {
    tag: 'Compliance Core',
    title: 'Entity Telemetry Protocol',
    description:
      'Leverage our proprietary verification engine to synchronize multi-jurisdictional entity data. We utilize a decentralized consensus model to validate certification requirements against federal frameworks, ensuring immutable compliance readiness.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/>
      </svg>
    ),
  },
  {
    tag: 'Global Trade & Logistics',
    title: 'Global Supply Chain Sync',
    description:
      'Unified infrastructure for Importers and Freight Forwarders. Manage multi-currency invoices and settle international supplier accounts instantly at wholesale conversion rates, integrating logistics data directly into the project budget.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
  },
];

const testimonials = [
  {
    quote:
      '"Historically, our cross-border capital was disconnected from our customs declarations, creating a massive blind spot for TBML and audit liability. LusidPay\'s conditional allocation engine solved this instantly. By mathematically tying our international disbursements directly to verified 19 CFR data, we automated our Form 28 defense."',
    author: '- Chief Financial Officer, Top 500 U.S. Importer',
  },
  {
    quote: '"The Trade Compliance OS acts as our ultimate liability shield. It ingests massive, unstructured BOMs and executes zero-hallucination semantic extraction before anything hits CBP ACE. Fusing that 19 CFR clearance with synchronized settlement has completely transformed how we manage global risk."',
    author: '– VP of Trade Compliance & Treasury, Global Logistics Firm',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const CommercePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // eslint-disable-next-line no-unused-vars
  const [activeSlide, setActiveSlide] = useState(0);
  const slideIntervalRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [location]);

  useEffect(() => {
    slideIntervalRef.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % 2);
    }, 6000);
    return () => clearInterval(slideIntervalRef.current);
  }, []);

  return (
    <Box className="commerce-page">

      {/* ── Hero ──────────────────────────────────────────────── */}
      <Box className="cp-hero">
        <Box className="cp-hero__grid-bg" />
        <Container maxWidth="lg" className="cp-hero__inner">
          <Typography className="cp-hero__title" sx={{ marginTop: '30px' }}>
            Unifying AI Compliance with{' '}
            <Box component="span" className="cp-hero__accent">
              Programmable Ledger Allocations.
            </Box>
          </Typography>
          <Typography className="cp-hero__subtitle">
            LusidPay engineers multi-billion dollar trade and logistics infrastructure. We pair zero-hallucination semantic extraction engines with conditional fund disbursements to eliminate regulatory liability and synchronize global settlement.
          </Typography>
          <Box className="cp-hero__cta-row">
            <Button
              variant="contained"
              className="cp-hero__btn cp-hero__btn--primary"
              onClick={() => navigate('/commerce/audit')}
            >
              Explore Lusid Audit
            </Button>
            <Button
              variant="outlined"
              className="cp-hero__btn cp-hero__btn--secondary"
              onClick={() => navigate('/commerce/platform-architecture')}
            >
              View Architecture
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── Deployed Nodes ────────────────────────────────────── */}
      <Box className="cp-nodes">
        <Container maxWidth="lg">
          <Box className="cp-nodes__header">
            <Typography className="cp-nodes__title">
              Deployed Across High-Stakes Nodes
            </Typography>
            <Typography className="cp-nodes__subtitle">
              Our conditional allocation engines are specifically tailored for complex, multi-party regulatory environments.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Box className="cp-node-card cp-node-card--live">
                <Box className="cp-node-card__header">
                  <Typography className="cp-node-card__sector cp-node-card__sector--sky">
                    Global Logistics
                  </Typography>
                  <Box className="cp-node-card__badge cp-node-card__badge--live">Live</Box>
                </Box>
                <Typography className="cp-node-card__name">Lusid Audit</Typography>
                <Typography className="cp-node-card__desc">
                  Built for Large Importers and Customs Brokers. Ingests 500-page BOMs, performs deterministic math against draft CBP Form 7501s to ensure absolute 19 CFR compliance, and generates cryptographic audit dossiers.
                </Typography>
                <Button
                  className="cp-node-card__link"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/commerce/audit')}
                  sx={{ padding: '20px' }}
                >
                  View Detail
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Features ──────────────────────────────────────────── */}
      <Box className="cp-features">
        <Container maxWidth="lg">
          <Box className="cp-features__header">
            <Typography className="cp-features__title">
              Unified Financial Infrastructure
            </Typography>
            <Typography className="cp-features__subtitle">
              Orchestrate complex capital flows with programmable logic. Replace disconnected spreadsheets/systems and static banking portals with an automated financial operating system.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {featureCards.map((card) => (
              <Grid item xs={12} md={6} key={card.title}>
                <Box className="cp-feature-card">
                  <Typography className="cp-feature-card__tag">{card.tag}</Typography>
                  <Box className="cp-feature-card__icon-box">{card.icon}</Box>
                  <Typography className="cp-feature-card__title">{card.title}</Typography>
                  <Typography className="cp-feature-card__body">{card.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Testimonials ──────────────────────────────────────── */}
      <Box className="cp-testimonials" id="testimonials">
        <Container maxWidth="md">
          <Typography className="cp-section-title">What Our Clients Say</Typography>
          {testimonials.map((t) => (
            <Box className="cp-testimonial-card" key={t.author}>
              <Typography className="cp-testimonial-card__quote">{t.quote}</Typography>
              <Typography className="cp-testimonial-card__author">{t.author}</Typography>
            </Box>
          ))}
        </Container>
      </Box>

      {/* ── CTA ───────────────────────────────────────────────── */}
      <Box className="cp-cta">
        <Container maxWidth="md">
          <Typography className="cp-cta__title">
            Ready to transform your finance operations?
          </Typography>
          <Button
            variant="contained"
            className="cp-cta__btn"
            onClick={() => navigate('/contact-us')}
          >
            Contact Sales
          </Button>
        </Container>
      </Box>

    </Box>
  );
};

export default CommercePage;
